import { AddReactionIcon } from "@rosette/web_components/src/icon/AddReactionIcon.js";
import { useReactionController } from "@rosette/web_components/src/game/reactions/ReactionUIContext.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { PopupButton } from "@/components/generic/popup/PopupButton.js";
import { useOptionalUser } from "@/components/env/EnvContext.js";
import { Role } from "@rosette/ts/src/business/role/Role.js";
import { ReactionType } from "@rosette/ts/src/business/game/ReactionType.js";
import { Permission } from "@rosette/ts/src/business/role/Permission.js";
import { ReactionIcon } from "@/components/icon/ReactionIcon.js";
import type { PlayerType } from "@rosette/ts/src/royalur/model/PlayerType.js";
import { useRef } from "react";
import type { PopupFunctions } from "@/components/generic/popup/Popup.js";
import { useRune } from "@rosette/react_utils/src/useRune.js";
import { motion, AnimatePresence } from "framer-motion";
import { fadeInOut } from "@/utils/fadeInOut.js";
import PremiumIcon from "@/components/icon/PremiumIcon.js";
import { useReactionResources } from "@/components/game/reactions/ReactionPlayback.js";


interface ReactionUIProps {
    yourPlayer: PlayerType | null;
    disabledReactions: boolean;
    youDisabledReactions: boolean;
    className: string;
}


export function ReactionUI({
    yourPlayer, disabledReactions, youDisabledReactions, className,
}: ReactionUIProps) {
    const user = useOptionalUser();
    const roles = Role.list(user);
    const hasMemburReactions = Permission.MEMBUR_REACTIONS.hasPermission(roles);

    const controller = useReactionController();
    const playingOwnReaction = useRune(controller.getPlayingOwnPlayerReaction());
    const resources = useReactionResources();

    const popupRef = useRef<PopupFunctions | null>(null);

    const reactionElems = [];
    const memburElems = [];
    for (const reaction of ReactionType.values()) {
        const category = reaction.getCategory();
        if (category !== "free" && category !== "memburs")
            continue;

        const hasAccess = (category === "free" || hasMemburReactions);
        const elem = (
            <div
                key={reaction.getID()}
                className={cn(
                    "p-2 rounded-2xl leading-[0] text-base w-[4em]",
                    "transition-transform duration-200 ease-[ease-in-out]",
                    hasAccess
                        ? "scale-100 cursor-pointer hover:scale-125"
                        : "scale-95 cursor-not-allowed grayscale opacity-50",
                )}
                onClick={() => {
                    if (!hasAccess) {
                        resources.getErrorSound().play(true);
                        return;
                    }

                    const popup = popupRef.current;
                    if (!popup)
                        throw new Error("No popup functions available");

                    popup.close();
                    setTimeout(() => {
                        controller.handleReaction(yourPlayer, reaction);
                    }, 350);
                }}>

                <ReactionIcon reaction={reaction} />
            </div>
        );
        if (category === "memburs") {
            memburElems.push(elem);
        } else {
            reactionElems.push(elem);
        }
    }

    return (
        <AnimatePresence>
            {!youDisabledReactions && (disabledReactions || !playingOwnReaction) && (
                <motion.div
                    key="reaction-button"
                    className={cn(
                        "z-10 px-1",
                        className,
                    )}
                    {...fadeInOut({ delay: 0.25 })}>

                    <PopupButton
                        buttonClassName="group"
                        popupRef={popupRef}
                        buttonContent={(
                            <AddReactionIcon className={cn(
                                "w-[2em] h-[2em] max-w-[2em] max-h-[2em] ml-auto opacity-80",
                                disabledReactions
                                    ? "fill-text-subtle"
                                    : "fill-text group-hover:fill-text-strong",
                            )} />
                        )}
                        contentClassName="p-0 overflow-hidden"
                        minPopupWidthPx={260}
                        darkMode={true}>

                        {!disabledReactions && (
                            <>
                                <div className="grid grid-cols-4 w-full">
                                    {reactionElems}
                                </div>

                                <div className="relative w-full bg-surface-sunken">
                                    {!hasMemburReactions && (
                                        <h3 className="px-3 pt-4 pb-0 m-0 text-base text-body">
                                            <PremiumIcon className="mr-2 -translate-y-[0.1em]" />
                                            Premium only
                                        </h3>
                                    )}

                                    <div className="grid grid-cols-4 w-full">
                                        {memburElems}
                                    </div>
                                </div>
                            </>
                        )}

                        {disabledReactions && (
                            <p className="p-4 m-0">
                                Your opponent has disabled reactions.
                            </p>
                        )}
                    </PopupButton>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
